import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatRUT } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Loader2, CheckCircle2, UserPlus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { SupportLine } from "@/components/SupportContact";

export default function Register() {
  const navigate = useNavigate();
  const { loginWith } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [rut, setRut] = useState("");
  const [lookup, setLookup] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [captcha, setCaptcha] = useState(null);
  const [captchaAnswer, setCaptchaAnswer] = useState("");

  const loadCaptcha = async () => {
    setCaptchaAnswer("");
    try {
      const { data } = await api.get("/auth/captcha");
      setCaptcha(data);
    } catch { setCaptcha(null); }
  };

  const doLookup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/lookup-rut", { rut });
      if (data.already_registered) {
        toast.error("Ya existe una cuenta con este RUT. Inicia sesión.");
        setLoading(false);
        return;
      }
      setLookup(data);
      if (data.name) setName(data.name);
      await loadCaptcha();
      setStep(2);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const sendCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/start-registration", {
        rut, email, name,
        captcha_token: captcha?.token, captcha_answer: captchaAnswer,
      });
      toast.success("Enviamos un código a tu email.");
      setStep(3);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      loadCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const complete = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Las contraseñas no coinciden."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/complete-registration", { rut, email, code, password, name });
      loginWith(data.token, data.user);
      toast.success("¡Cuenta creada!");
      navigate("/panel");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-5 py-12">
      <div className="w-full max-w-md fade-in">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <ShieldCheck className="w-7 h-7 text-primary" />
          <span className="font-heading font-extrabold text-xl text-primary">EIM Becas</span>
        </div>
        <div className="bg-white rounded-2xl border border-border p-8">
          {step === 1 && (
            <form onSubmit={doLookup} className="space-y-5">
              <h1 className="text-2xl font-bold">Iniciar solicitud</h1>
              <p className="text-muted-foreground">Comencemos con tu RUT.</p>
              <div>
                <Label className="text-sm font-semibold uppercase tracking-wide text-slate-600">RUT</Label>
                <Input data-testid="input-rut" className="h-14 text-lg rounded-xl mt-2" placeholder="12.345.678-9"
                  value={rut} onChange={(e) => setRut(formatRUT(e.target.value))} required />
              </div>
              <Button type="submit" disabled={loading} className="btn-lift w-full h-14 text-lg rounded-xl" data-testid="btn-lookup-rut">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Continuar"}
              </Button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={sendCode} className="space-y-5">
              {lookup?.in_eim_base ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex gap-3" data-testid="lookup-found">
                  <CheckCircle2 className="w-6 h-6 text-emerald-700 shrink-0" />
                  <div>
                    <p className="font-semibold text-emerald-900">Hola, {lookup.name}.</p>
                    <p className="text-sm text-emerald-800">Encontramos tu registro. Verifica tu email para continuar.</p>
                  </div>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3" data-testid="lookup-not-found">
                  <UserPlus className="w-6 h-6 text-amber-700 shrink-0" />
                  <p className="text-sm text-amber-900">
                    No encontramos tu RUT en nuestra base de datos. No hay problema, igualmente puedes iniciar una solicitud.
                  </p>
                </div>
              )}
              {!lookup?.in_eim_base && (
                <div>
                  <Label className="text-sm font-semibold uppercase tracking-wide text-slate-600">Nombre completo</Label>
                  <Input data-testid="input-name" className="h-14 text-lg rounded-xl mt-2" value={name}
                    onChange={(e) => setName(e.target.value)} required />
                </div>
              )}
              <div>
                <Label className="text-sm font-semibold uppercase tracking-wide text-slate-600">Email</Label>
                <Input data-testid="input-email" type="email" className="h-14 text-lg rounded-xl mt-2" value={email}
                  onChange={(e) => setEmail(e.target.value)} required />
              </div>
              <div>
                <Label className="text-sm font-semibold uppercase tracking-wide text-slate-600">Verificación de seguridad</Label>
                <div className="flex items-center gap-3 mt-2">
                  <div className="flex items-center gap-2 h-14 px-4 rounded-xl bg-secondary border border-border font-mono text-lg font-bold select-none" data-testid="captcha-question">
                    {captcha ? `${captcha.question} =` : "…"}
                    <button type="button" onClick={loadCaptcha} className="text-muted-foreground hover:text-primary" data-testid="btn-captcha-refresh" title="Otro desafío">
                      <RefreshCw className="w-4 h-4" />
                    </button>
                  </div>
                  <Input data-testid="input-captcha" inputMode="numeric" className="h-14 text-lg rounded-xl flex-1" placeholder="Resultado"
                    value={captchaAnswer} onChange={(e) => setCaptchaAnswer(e.target.value)} required />
                </div>
                <p className="text-xs text-muted-foreground mt-1">Resuelve la suma para confirmar que no eres un robot.</p>
              </div>
              <Button type="submit" disabled={loading} className="btn-lift w-full h-14 text-lg rounded-xl" data-testid="btn-send-code">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Enviar código de verificación"}
              </Button>
            </form>
          )}

          {step === 3 && (
            <form onSubmit={complete} className="space-y-5">
              <h1 className="text-2xl font-bold">Verifica y crea tu contraseña</h1>
              <p className="text-muted-foreground">Ingresa el código de 6 dígitos que enviamos a {email}.</p>
              <div>
                <Label className="text-sm font-semibold uppercase tracking-wide text-slate-600">Código</Label>
                <Input data-testid="input-code" className="h-14 text-lg rounded-xl mt-2 tracking-widest" value={code}
                  onChange={(e) => setCode(e.target.value)} maxLength={6} required />
              </div>
              <div>
                <Label className="text-sm font-semibold uppercase tracking-wide text-slate-600">Contraseña</Label>
                <Input data-testid="input-password" type="password" className="h-14 text-lg rounded-xl mt-2" value={password}
                  onChange={(e) => setPassword(e.target.value)} required />
              </div>
              <div>
                <Label className="text-sm font-semibold uppercase tracking-wide text-slate-600">Confirmar contraseña</Label>
                <Input data-testid="input-confirm" type="password" className="h-14 text-lg rounded-xl mt-2" value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} required />
              </div>
              <Button type="submit" disabled={loading} className="btn-lift w-full h-14 text-lg rounded-xl" data-testid="btn-create-account">
                {loading ? <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Buscando tu información en la base…</> : "Crear cuenta"}
              </Button>
              {loading && (
                <p className="text-center text-sm text-muted-foreground" data-testid="create-account-wait">
                  Estamos verificando tu código y buscando tus datos en la base del EIM. Esto puede tardar unos segundos, no cierres esta ventana.
                </p>
              )}
            </form>
          )}

          <p className="text-center text-sm mt-6 text-muted-foreground">
            ¿Ya tienes cuenta? <Link to="/ingresar" className="text-primary font-semibold hover:underline">Ingresar</Link>
          </p>
        </div>
        <SupportLine className="mt-6" />
      </div>
    </div>
  );
}
